'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useBodyScrollLock } from './useBodyScrollLock';
import { useChaptersContentQuery } from './useChaptersContentQuery';
import { useFanficNavigation } from './useFanficNavigation';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { useReaderChaptersQuery } from './useReaderChaptersQuery';
import type { Chapter, TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';

const READER_CHAPTERS_CONTENT_LIMIT = 5;

export interface UseFanficPageReaderProps {
  workId: string;
  chaptersCount: number;
  workPathPrefix?: '/fanfics' | '/originals';
}

export interface CloseFullscreenReaderOptions {
  restoreHistory?: boolean;
}

export interface UseFanficPageReaderReturn {
  selectedChapterId: string | null;
  setSelectedChapterId: (chapterId: string | null) => void;
  isFullscreenOpen: boolean;
  setIsFullscreenOpen: (isOpen: boolean) => void;
  closeFullscreenReader: (options?: CloseFullscreenReaderOptions) => void;
  readerChapters: TChapterWithoutContent[];
  chaptersWithContent: Chapter[];
  isLoadingChapters: boolean;
  handleLoadMoreChapters: (direction: 'next' | 'prev') => Promise<void>;
  handleChapterSelect: (chapterId: string) => Promise<void>;
  handleOpenFullscreen: (chapterId: string) => Promise<void>;
}

const buildChapterReaderPath = (
  workPathPrefix: string,
  workId: string,
  chapterId: string,
): string => `${workPathPrefix}/${workId}/read/${chapterId}`;

const publishChapterReaderUrl = (nextUrl: string, hasReaderHistoryEntry: boolean): boolean => {
  if (typeof window === 'undefined' || window.location.pathname === nextUrl) {
    return hasReaderHistoryEntry;
  }

  if (hasReaderHistoryEntry) {
    window.history.replaceState(null, '', nextUrl);
    return true;
  }

  window.history.pushState(null, '', nextUrl);
  return true;
};

const resolveChapterContentPage = (chapterIndex: number): number =>
  Math.floor(chapterIndex / READER_CHAPTERS_CONTENT_LIMIT) + 1;

/**
 * Fullscreen reader state, chapter loading, and keyboard/scroll side effects.
 */
export const useFanficPageReader = ({
  workId,
  chaptersCount,
  workPathPrefix = '/fanfics',
}: UseFanficPageReaderProps): UseFanficPageReaderReturn => {
  const [selectedChapterId, setSelectedChapterIdState] = useState<string | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpenState] = useState(false);
  const readerHistoryPushedRef = useRef(false);

  const { readerChapters, fetchAllReaderChapters } = useReaderChaptersQuery({
    workId,
    chaptersCount,
  });

  const {
    chapters: chaptersWithContent,
    isLoading: isLoadingChapters,
    fetchChapters: fetchChaptersContent,
    loadMore: loadMoreChapters,
    currentPage,
  } = useChaptersContentQuery({
    workId,
    limit: READER_CHAPTERS_CONTENT_LIMIT,
  });

  const setSelectedChapterId = useCallback(
    (chapterId: string | null) => {
      setSelectedChapterIdState(chapterId);
      if (!chapterId || !readerHistoryPushedRef.current) {
        return;
      }

      readerHistoryPushedRef.current = publishChapterReaderUrl(
        buildChapterReaderPath(workPathPrefix, workId, chapterId),
        readerHistoryPushedRef.current,
      );
    },
    [workId, workPathPrefix],
  );

  const { goToPreviousChapter, goToNextChapter, canGoNext, canGoPrevious } = useFanficNavigation({
    chapters: readerChapters,
    selectedChapterId,
    setSelectedChapterId,
  });

  const closeFullscreenReader = useCallback((options?: CloseFullscreenReaderOptions) => {
    setIsFullscreenOpenState(false);
    if (!readerHistoryPushedRef.current) {
      return;
    }

    readerHistoryPushedRef.current = false;
    if (options?.restoreHistory === false) {
      return;
    }

    window.history.back();
  }, []);

  const setIsFullscreenOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        closeFullscreenReader();
        return;
      }

      setIsFullscreenOpenState(true);
    },
    [closeFullscreenReader],
  );

  useBodyScrollLock(isFullscreenOpen);

  useKeyboardNavigation({
    onLeftArrow: goToPreviousChapter,
    onRightArrow: goToNextChapter,
    onEscape: closeFullscreenReader,
    canGoLeft: canGoPrevious,
    canGoRight: canGoNext,
    canEscape: isFullscreenOpen,
  });

  useEffect(() => {
    const handlePopState = () => {
      if (!readerHistoryPushedRef.current) {
        return;
      }

      readerHistoryPushedRef.current = false;
      setIsFullscreenOpenState(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLoadMoreChapters = useCallback(
    async (direction: 'next' | 'prev') => {
      if (direction === 'next') {
        await loadMoreChapters();
        return;
      }

      if (currentPage > 1) {
        await fetchChaptersContent(currentPage - 1);
      }
    },
    [loadMoreChapters, fetchChaptersContent, currentPage],
  );

  const handleChapterSelect = useCallback(
    async (chapterId: string) => {
      const hasContent = chaptersWithContent.some((chapter) => chapter.id === chapterId);
      if (hasContent) {
        return;
      }

      const chapterIndex = readerChapters.findIndex((chapter) => chapter.id === chapterId);
      if (chapterIndex === -1) {
        return;
      }

      await fetchChaptersContent(resolveChapterContentPage(chapterIndex));
    },
    [readerChapters, chaptersWithContent, fetchChaptersContent],
  );

  const handleOpenFullscreen = useCallback(
    async (chapterId: string) => {
      setSelectedChapterIdState(chapterId);
      setIsFullscreenOpenState(true);
      readerHistoryPushedRef.current = publishChapterReaderUrl(
        buildChapterReaderPath(workPathPrefix, workId, chapterId),
        readerHistoryPushedRef.current,
      );

      const loadedChapters = await fetchAllReaderChapters();
      const chapterIndex = loadedChapters.findIndex((chapter) => chapter.id === chapterId);
      if (chapterIndex === -1) {
        return;
      }

      await fetchChaptersContent(resolveChapterContentPage(chapterIndex));
    },
    [fetchAllReaderChapters, fetchChaptersContent, workId, workPathPrefix],
  );

  return {
    selectedChapterId,
    setSelectedChapterId,
    isFullscreenOpen,
    setIsFullscreenOpen,
    closeFullscreenReader,
    readerChapters,
    chaptersWithContent,
    isLoadingChapters,
    handleLoadMoreChapters,
    handleChapterSelect,
    handleOpenFullscreen,
  };
};
